/**
 * The backlog tree with bulk selection: checkboxes on hover (Shift extends, Ctrl toggles, x on
 * the keyboard), and the bottom action bar while anything is picked (BacklogBulk).
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import type { BacklogItem, Id, QualityIssue } from '@orgus/protocol';
import { cn, useSelection } from '@orgus/ui';
import type { ReqFilter } from '../requirements/model.ts';
import { TreeView } from '../requirements/TreeView.tsx';
import { BacklogBulk } from './BacklogBulk.tsx';
import type { MissionRow } from './model.ts';

export function BacklogTree({
  items,
  filter,
  rows,
  issues,
  selectedId,
  owners,
  projectName,
  showProject,
  onOpen,
  onAddChild,
  onStart,
  onStartMany,
  onStartSequence,
  onOpenMission,
}: {
  items: readonly BacklogItem[];
  filter: ReqFilter;
  rows: readonly MissionRow[];
  issues: ReadonlyMap<Id, QualityIssue[]>;
  selectedId: Id | null;
  owners: readonly string[];
  projectName: (id: Id) => string;
  showProject: boolean;
  onOpen: (id: Id) => void;
  onAddChild: (parent: BacklogItem) => void;
  onStart: (item: BacklogItem) => void;
  onStartMany?: (items: BacklogItem[]) => void;
  onStartSequence: (item: BacklogItem) => void;
  onOpenMission: (missionId: Id) => void;
}) {
  const order = useMemo(() => items.map((i) => i.id), [items]);
  const shown = useRef<Id[]>([]);
  const sel = useSelection(order, { displayOrder: () => shown.current });
  const setShown = useCallback((ids: Id[]) => {
    shown.current = ids;
  }, []);
  const [deleting, setDeleting] = useState<BacklogItem[] | null>(null);
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const picked = useMemo(() => [...sel.selected].map((id) => byId.get(id)).filter((i): i is BacklogItem => !!i), [sel.selected, byId]);

  return (
    // While the action bar shows, the list keeps room under its last row so the bar never hides it.
    <div className={cn('flex flex-col gap-2', sel.count > 0 && 'pb-16')}>
      <BacklogBulk picked={picked} allItems={items} rows={rows} owners={owners} total={shown.current.length} onSelectAll={sel.toggleAll} onClear={sel.clear} onStart={onStart} onStartMany={onStartMany} deleting={deleting} onDeleting={setDeleting} />
      <TreeView
        items={items}
        filter={filter}
        rows={rows}
        issues={issues}
        selectedId={selectedId}
        onOpen={onOpen}
        onAddChild={onAddChild}
        projectName={projectName}
        showProject={showProject}
        actions={{ onStart, onStartSequence, onOpenMission, onDelete: (it) => setDeleting([it]) }}
        selection={sel}
        onOrder={setShown}
      />
    </div>
  );
}
